import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { createRoute, PATHS } from '@core/router/paths';
import useCheckSubscription from '@shared/hooks/use-check-subscription';

/**
 * SubscriptionGuard
 *
 * Ensures users have an active subscription before accessing protected routes.
 * Users without a subscription are redirected to the billing page and cannot
 * navigate away until they have a valid subscription.
 *
 * @example
 * <SubscriptionGuard>
 *   <ProtectedRoute />
 * </SubscriptionGuard>
 */
export function SubscriptionGuard({ children }: { children: ReactNode }) {
  const location = useLocation();

  const { subscription, isFreeTrial, isTrialCompleted } =
    useCheckSubscription();

  const billingPath = createRoute(
    `${PATHS.SETTINGS_BILLING_A_SUBSCRIPTION}?tab=upgrade`
  );

  const isOnBillingRoute = location.pathname.includes(
    PATHS.SETTINGS_BILLING_A_SUBSCRIPTION
  );

  const needsBillingRedirect =
    !subscription?.id || isFreeTrial || isTrialCompleted;

  if (needsBillingRedirect && !isOnBillingRoute) {
    return (
      <Navigate
        to={billingPath}
        replace
      />
    );
  }

  return <>{children}</>;
}
